import type { Theme } from 'unocss/preset-uno'

export type ThemeName = 'light' | 'dark'

const lightColors = {
  // brand
  'brand-primary': '#CF0073',
  'brand-primary-5': 'rgba(207,0,115,0.05)',
  'brand-primary-hover': '#D92783',
  'eol-brand-global': '#FDCB67',
  'eol-brand-heavy': '#A56717',
  'eol-brand-light': '#FEE7A7',
  // rainbow
  red: '#D92629',
  'red-hover': '#B61820',
  orange: '#E65C00',
  'orange-hover': '#C04600',
  yellow: '#EDC801',
  'yellow-hover': '#C5A101',
  green: '#38A700',
  'green-hover': '#2B9000',
  cyan: '#00ABCC',
  'cyan-hover': '#008AAC',
  blue: '#126ADF',
  'blue-hover': '#5FA6EC',
  indigo: '#4130F2',
  'indigo-hover': '#261EC9',
  'gp-1-normal': '#5B49FF',
  'gp-1-hover': '#7D69FF',
  'gp-2-normal': '#CF0073',
  'gp-2-hover': '#D92783',
  'gp-3-normal': '#E0726C',
  'gp-3-hover': '#D64B49',
  // text
  'text-invert': '#FFFFFF',
  'text-primary': '#101112',
  'text-secondary': '#55575E',
  'text-tertiary': '#9EA2AA',
  'text-disabled': '#BDC0C7',
  // background
  'bg-surface': '#FFFFFF',
  'bg-surground': '#F8FAFB',
  'bg-ground': '#F0F2F7',
  'bg-bubble-0': '#3E3D45',
  'bg-tooltip': '#000000',
  'bg-table-head': '#F5F6F7',
  'bg-table-item': 'rgba(6, 27, 51, .02)',
  'bg-overlay-0': 'rgba(6, 27, 51, 0)',
  'bg-overlay-1': 'rgba(6, 27, 51, .04)',
  'bg-overlay-2': 'rgba(6, 27, 51, .08)',
  'bg-overlay-3': 'rgba(6, 27, 51, .12)',
  'bg-selection-string': 'rgba(18, 106, 223, 0.1)',
  'bg-shadow': 'rgba(6, 27, 51, 1)',
  // stroke
  'stroke-1': '#EBEEF4',
  'stroke-2': '#DADEE7',
  'stroke-3': '#BDC0C7',
  'stroke-button-primary': 'rgba(255, 255, 255, 0.40)',
}

const darkColors = {
  // brand
  'brand-primary': '#EC7DAF',
  'brand-primary-5': 'rgba(236,125,175,0.05)',
  'brand-primary-hover': '#F5B0CC',
  'eol-brand-global': '#FEDB8C',
  'eol-brand-heavy': '#FDBF56',
  'eol-brand-light': '#FFF2CA',
  // rainbow
  red: '#E8746E',
  'red-hover': '#F09D96',
  orange: '#F09C52',
  'orange-hover': '#F5BB82',
  yellow: '#F2D75B',
  'yellow-hover': '#F7E68B',
  green: '#7FCA4D',
  'green-hover': '#A5DC7B',
  cyan: '#55D2E0',
  'cyan-hover': '#84E3EB',
  blue: '#5FA6EC',
  'blue-hover': '#8AC2F2',
  indigo: '#8E7BF7',
  'indigo-hover': '#B2A2FA',
  'gp-1-normal': '#BEACFF',
  'gp-1-hover': '#D7CBFF',
  'gp-2-normal': '#EC7DAF',
  'gp-2-hover': '#F5B0CC',
  'gp-3-normal': '#EB9F98',
  'gp-3-hover': '#F5C8C3',
  // text
  'text-invert': '#000000',
  'text-primary': '#F0F2F7',
  'text-secondary': '#BDC0C7',
  'text-tertiary': '#676B73',
  'text-disabled': '#505259',
  // background
  'bg-surface': '#1C1D1F',
  'bg-surground': '#17181A',
  'bg-ground': '#131314',
  'bg-bubble-0': '#C3C2CC',
  'bg-tooltip': '#000000',
  'bg-table-head': '#252628',
  'bg-table-item': 'rgba(255, 255, 255, .02)',
  'bg-overlay-0': 'rgba(255, 255, 255, 0)',
  'bg-overlay-1': 'rgba(255, 255, 255, .04)',
  'bg-overlay-2': 'rgba(255, 255, 255, .08)',
  'bg-overlay-3': 'rgba(255, 255, 255, .12)',
  'bg-selection-string': 'rgba(18, 106, 223, 0.1)',
  'bg-shadow': 'rgba(12, 27, 51, 1)',
  // stroke
  'stroke-1': '#242629',
  'stroke-2': '#2F3036',
  'stroke-3': '#383940',
  'stroke-button-primary': 'rgba(255, 255, 255, 0.2)',
}

const sharedTokens = {
  fontFamily: {
    genryu: ['GenRyuMin2 JP'],
  },
}

export const theme: Record<ThemeName, Theme> = {
  light: {
    colors: lightColors,
    boxShadow: {
      sm: '0px 6px 12px 0px rgba(6, 27, 51,.1), 0px 2px 6px 0px rgba(6, 27, 51,.1)',
      md: '0px 12px 32px 0px rgba(6, 27, 51, .1), 0px 4px 12px 0px rgba(6, 27, 51, .1)',
      lg: '0 8px 20px 0 rgba(6, 27, 51, 0.1), 0 20px 48px 0 rgba(6, 27, 51, 0.1)',
      focus: '0 0 0 4px rgba(207, 0, 15, 0.1)',
      bookcover: '2px 2px 5px 0px rgba(6, 27, 51, 0.15), 7px 7px 10px 0px rgba(6, 27, 51, 0.13), 16px 16px 13px 0px rgba(6, 27, 51, 0.08), 28px 28px 16px 0px rgba(6, 27, 51, 0.02)',
      compositionhover: '0 0 0 4px rgba(6, 27, 51, 0.1)',
      finalcomposition: '0 0 0 4px rgba(207, 0, 15, 0.1), 0px 12px 32px 0px rgba(6, 27, 51, .1), 0px 4px 12px 0px rgba(6, 27, 51, .1)',
      finalcompositionhover: '0 0 0 4px rgba(236, 125, 175, 0.3), 0px 12px 32px 0px rgba(6, 27, 51, .1), 0px 4px 12px 0px rgba(6, 27, 51, .1)',
    },
    ...sharedTokens,
  },
  dark: {
    colors: darkColors,
    boxShadow: {
      sm: '0px 6px 12px 0px rgba(12, 13, 13,.4), 0px 2px 6px 0px rgba(12, 13, 13,.4)',
      md: '0px 12px 32px 0px rgba(12, 13, 13, .4), 0px 4px 12px 0px rgba(12, 13, 13, .4)',
      lg: '0px 20px 48px 0px rgba(12, 13, 13, .4), 0px 8px 20px 0px rgba(12, 13, 13, .4)',
      focus: '0 0 0 4px rgba(236, 125, 175, 0.2)',
      bookcover: '2px 2px 5px 0px rgba(12, 13, 13, 0.45), 7px 7px 10px 0px rgba(12, 13, 13, 0.43), 16px 16px 13px 0px rgba(12, 13, 13, 0.38), 28px 28px 16px 0px rgba(12, 13, 13, 0.32)',
      compositionhover: '0 0 0 4px rgba(255, 255, 255, 0.04)',
      finalcomposition: '0 0 0 4px rgba(236, 125, 175, 0.2), 0px 12px 32px 0px rgba(12, 13, 13, .4), 0px 4px 12px 0px rgba(12, 13, 13, .4)',
      finalcompositionhover: '0 0 0 4px rgba(236, 125, 175, 0.4), 0px 12px 32px 0px rgba(12, 13, 13, .4), 0px 4px 12px 0px rgba(12, 13, 13, .4)',
    },
    ...sharedTokens,
  },
}

const THEME_STYLE_ID = 'theme-css-variables'

const ensureStyleElement = (): HTMLStyleElement | null => {
  if (typeof document === 'undefined') return null
  let style = document.getElementById(THEME_STYLE_ID) as HTMLStyleElement | null
  if (!style) {
    style = document.createElement('style')
    style.id = THEME_STYLE_ID
    document.head.appendChild(style)
  }
  return style
}

export function generateCSSVariables(themeName: ThemeName) {
  const currentTheme = theme[themeName]
  if (!currentTheme) return

  const style = ensureStyleElement()
  if (!style) return

  const colorLines = Object.entries(currentTheme.colors ?? {}).map(
    ([key, value]) => `  --${key}: ${value};`
  )

  const shadowLines = Object.entries(currentTheme.boxShadow ?? {}).map(
    ([key, value]) => `  --shadow-${key}: ${value};`
  )

  style.textContent = [
    ':root {',
    ...colorLines,
    ...shadowLines,
    `  color-scheme: ${themeName === 'dark' ? 'dark' : 'light'};`,
    '}',
  ].join('\n')
}
